import { ApiClient, ApiRequestError, RealtimeClient } from '@deskworks/client';
import { ClientProvider, createQueryClient } from '@deskworks/client/react';
import type { SessionInfo } from '@deskworks/domain';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useThemeSync } from './theme.js';
import { clearTrusted } from './trusted-store.js';
import { unsubscribeWebPush } from './platform.js';

export const api = new ApiClient({ baseUrl: '', mode: 'cookie' });

type Status = 'loading' | 'signed-in' | 'signed-out';

interface SessionContextValue {
  status: Status;
  session: SessionInfo | null;
  /** Why the person was signed out (expired or revoked elsewhere), shown on the sign-in page. */
  endedReason: string | null;
  setSession: (s: SessionInfo) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/* Unsaved-work registry: composers register while they hold work not yet saved to the server. */
const unsaved = new Map<string, string>();
export function registerUnsaved(key: string, description: string | null) {
  if (description) unsaved.set(key, description);
  else unsaved.delete(key);
}
export function unsavedWork(): string[] {
  return [...unsaved.values()];
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (unsaved.size) {
      e.preventDefault();
    }
  });
}

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('dw-session') : null;

/**
 * Browser session boundary. The session token lives only in an HttpOnly cookie. Signing out (or
 * being signed out remotely) clears every app-controlled private cache in all open tabs.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSessionState] = useState<SessionInfo | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const queryClient = useMemo(() => createQueryClient(), []);
  const realtimeRef = useRef<RealtimeClient | null>(null);
  const [realtime, setRealtime] = useState<RealtimeClient | null>(null);

  useThemeSync(session?.settings.theme);

  const clearPrivateState = useCallback(
    (reason: string | null) => {
      realtimeRef.current?.stop();
      realtimeRef.current = null;
      setRealtime(null);
      queryClient.clear();
      api.csrfToken = null;
      unsaved.clear();
      void clearTrusted();
      // This browser stops receiving pushes for the signed-out person.
      void unsubscribeWebPush().catch(() => undefined);
      setSessionState(null);
      setEndedReason(reason);
      setStatus('signed-out');
    },
    [queryClient],
  );

  const setSession = useCallback((s: SessionInfo) => {
    setSessionState((prev) => {
      if (prev && prev.user.id !== s.user.id) queryClient.clear();
      return s;
    });
    setEndedReason(null);
    setStatus('signed-in');
  }, [queryClient]);

  const refresh = useCallback(async () => {
    try {
      setSession(await api.session());
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) clearPrivateState(null);
      else setStatus((s) => (s === 'loading' ? 'signed-out' : s));
    }
  }, [setSession, clearPrivateState]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Expired or revoked sessions stop new retrieval and clear visible private state.
  useEffect(() => {
    api.opts.onUnauthenticated = () => {
      if (status === 'signed-in') {
        clearPrivateState('Your session ended. Sign in again to continue.');
        channel?.postMessage({ type: 'signed-out' });
      }
    };
  }, [status, clearPrivateState]);

  useEffect(() => {
    if (!channel) return;
    const onMsg = (e: MessageEvent<{ type: string; userId?: string }>) => {
      if (e.data.type === 'signed-out') clearPrivateState('You signed out in another tab.');
      if (e.data.type === 'signed-in' && e.data.userId !== session?.user.id) void refresh();
    };
    channel.addEventListener('message', onMsg);
    return () => channel.removeEventListener('message', onMsg);
  }, [clearPrivateState, refresh, session?.user.id]);

  // Realtime lifecycle follows the session.
  useEffect(() => {
    if (status !== 'signed-in' || realtimeRef.current) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const rt = new RealtimeClient({
      url: `${proto}://${location.host}/v1/realtime`,
      getTicket: async () => (await api.realtimeTicket()).ticket,
      onRevoked: () => {
        clearPrivateState('This device was signed out.');
        channel?.postMessage({ type: 'signed-out' });
      },
    });
    realtimeRef.current = rt;
    setRealtime(rt);
    rt.start();
    const nudge = () => rt.nudge();
    window.addEventListener('online', nudge);
    document.addEventListener('visibilitychange', nudge);
    return () => {
      window.removeEventListener('online', nudge);
      document.removeEventListener('visibilitychange', nudge);
    };
  }, [status, clearPrivateState]);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } catch {
      // Even if the server is unreachable, clear local private state.
    }
    clearPrivateState(null);
    channel?.postMessage({ type: 'signed-out' });
  }, [clearPrivateState]);

  useEffect(() => {
    if (status === 'signed-in' && session) channel?.postMessage({ type: 'signed-in', userId: session.user.id });
  }, [status, session]);

  const value = useMemo<SessionContextValue>(
    () => ({ status, session, endedReason, setSession, refresh, signOut }),
    [status, session, endedReason, setSession, refresh, signOut],
  );

  return (
    <SessionContext.Provider value={value}>
      <ClientProvider api={api} realtime={realtime} queryClient={queryClient}>
        {children}
      </ClientProvider>
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('SessionProvider missing');
  return ctx;
}

/** The signed-in session. Only use below the authenticated route boundary. */
export function useMe(): SessionInfo {
  const { session } = useSession();
  if (!session) throw new Error('Not signed in');
  return session;
}
